/**
 * Date utility functions for chart labels
 * Ensures consistent, readable date formatting across all WHOOP and Garmin charts
 */

/**
 * Generate a date range array for consistent chart x-axis labels
 * Creates an array of ISO date strings (YYYY-MM-DD) for the last N days including today
 * 
 * @param numDays - Number of days to generate (default: 30)
 * @returns Array of ISO date strings in ascending order
 */
export const generateDateRange = (numDays: number): string[] => {
  const results: string[] = [];
  for (let i = numDays - 1; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    // Include today as the last label (no +1 offset)
    d.setDate(d.getDate() - i);
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().split('T')[0];
    results.push(iso);
  }
  return results;
};

/**
 * Format date labels for chart display
 * Converts ISO dates and already-formatted dates to consistent format
 * 
 * @param label - Date string in ISO format (YYYY-MM-DD) or already formatted
 * @returns Formatted date string like "Nov 8"
 */
export const formatDateLabel = (label: string): string => {
  try {
    // If it's an ISO date (YYYY-MM-DD), format it
    if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
      const d = new Date(label + 'T00:00:00Z');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    // If it's already formatted (e.g., "Nov 8"), return as is
    // If it's some other date string, try to parse and format
    const date = new Date(label);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  } catch (e) {
    // Fallback to original label
  }
  return label;
};

/**
 * Format date for chart labels
 * Ensures consistent, readable date formatting across all charts
 * 
 * @param dateStr - Date string in ISO format or other parseable format
 * @returns Formatted date string like "Nov 8"
 */
export const formatChartDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateStr;
  }
};

/**
 * Generate unique date labels for a date range
 * Prevents duplicate labels on X-axis
 * 
 * @param dates - Array of date strings
 * @returns Array of unique formatted date labels
 */
export const generateUniqueDateLabels = (dates: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  
  dates.forEach(date => {
    const formatted = formatChartDate(date);
    let label = formatted;
    let counter = 1;
    
    // If duplicate, append counter
    while (seen.has(label)) {
      label = `${formatted} (${counter++})`;
    }
    
    seen.add(label);
    unique.push(label);
  });
  
  return unique;
};

/**
 * Format date with day shift for recovery data
 * Recovery data from last night should show under today's date
 * 
 * @param dateStr - Date string in ISO format
 * @returns Formatted date string shifted by one day
 */
export const formatRecoveryChartDate = (dateStr: string): string => {
  try {
    const displayDate = new Date(dateStr + 'T00:00:00Z');
    // Shift forward one day to show recovery under the day it applies to
    displayDate.setUTCDate(displayDate.getUTCDate() + 1);
    
    return displayDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return formatChartDate(dateStr);
  }
};

